import { useEffect, useMemo, useState } from 'react';
import { incidentsApi } from '../../services/incidentsApi';
import type {
  Incident,
  IncidentActionSnapshotRollupResponse,
  IncidentCorrectiveActionRollupResponse,
  IncidentEscalationRollupResponse,
  IncidentReviewOutcomeHistoryResponse,
  IncidentMeasurementRollupResponse,
  IncidentOwnershipRollupResponse,
  IncidentReviewOutcomeRollupResponse,
} from '../../services/incidentsApi';
import { toIsoFromLocalInput } from '../incidents/useIncidentsTimeRange';

type UseAcceptanceProofRollupsArgs = {
  hasScope: boolean;
  scopedOrgId?: string | null;
  scopedSiteId?: string | null;
  canonicalProofSiteId?: string | null;
  canonicalProofWindowStart?: string | null;
  canonicalProofWindowEnd?: string | null;
  canonicalProofIncidentType?: string | null;
  reportPolicyType?: string | null;
  canonicalProofZoneId?: string | null;
  reportZoneId?: string | null;
  canonicalProofDeviceId?: string | null;
  reportDeviceId?: string | null;
};

function normalizedScopeValue(value?: string | null): string {
  return String(value || '').trim();
}

export function useAcceptanceProofRollups({
  hasScope,
  scopedOrgId,
  scopedSiteId,
  canonicalProofSiteId,
  canonicalProofWindowStart,
  canonicalProofWindowEnd,
  canonicalProofIncidentType,
  reportPolicyType,
  canonicalProofZoneId,
  reportZoneId,
  canonicalProofDeviceId,
  reportDeviceId,
}: UseAcceptanceProofRollupsArgs) {
  const [reviewScopeIncidents, setReviewScopeIncidents] = useState<Incident[]>([]);
  const [reviewScopeIncidentsLoading, setReviewScopeIncidentsLoading] = useState(false);
  const [reviewOutcomeRollup, setReviewOutcomeRollup] = useState<IncidentReviewOutcomeRollupResponse | null>(null);
  const [reviewOutcomeRollupLoading, setReviewOutcomeRollupLoading] = useState(false);
  const [reviewOutcomeHistory, setReviewOutcomeHistory] = useState<IncidentReviewOutcomeHistoryResponse | null>(null);
  const [reviewOutcomeHistoryLoading, setReviewOutcomeHistoryLoading] = useState(false);
  const [ownershipRollup, setOwnershipRollup] = useState<IncidentOwnershipRollupResponse | null>(null);
  const [ownershipRollupLoading, setOwnershipRollupLoading] = useState(false);
  const [correctiveActionRollup, setCorrectiveActionRollup] = useState<IncidentCorrectiveActionRollupResponse | null>(null);
  const [correctiveActionRollupLoading, setCorrectiveActionRollupLoading] = useState(false);
  const [measurementRollup, setMeasurementRollup] = useState<IncidentMeasurementRollupResponse | null>(null);
  const [measurementRollupLoading, setMeasurementRollupLoading] = useState(false);
  const [actionSnapshotRollup, setActionSnapshotRollup] = useState<IncidentActionSnapshotRollupResponse | null>(null);
  const [actionSnapshotRollupLoading, setActionSnapshotRollupLoading] = useState(false);
  const [escalationRollup, setEscalationRollup] = useState<IncidentEscalationRollupResponse | null>(null);
  const [escalationRollupLoading, setEscalationRollupLoading] = useState(false);

  const scope = useMemo(() => {
    const siteId = normalizedScopeValue(canonicalProofSiteId || scopedSiteId);
    const fromRaw = normalizedScopeValue(canonicalProofWindowStart);
    const toRaw = normalizedScopeValue(canonicalProofWindowEnd);
    const fromIso = toIsoFromLocalInput(fromRaw) || fromRaw;
    const toIso = toIsoFromLocalInput(toRaw) || toRaw;
    const zoneId = normalizedScopeValue(canonicalProofZoneId || reportZoneId) || undefined;
    const deviceId = normalizedScopeValue(canonicalProofDeviceId || reportDeviceId) || undefined;
    const incidentType = normalizedScopeValue(canonicalProofIncidentType || reportPolicyType) || undefined;
    const orgId = normalizedScopeValue(scopedOrgId);
    const enabled = Boolean(hasScope && orgId && siteId && fromIso && toIso);
    return {
      enabled,
      orgId,
      siteId,
      fromIso,
      toIso,
      zoneId,
      deviceId,
      incidentType,
    };
  }, [
    canonicalProofDeviceId,
    canonicalProofIncidentType,
    canonicalProofSiteId,
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    canonicalProofZoneId,
    hasScope,
    reportDeviceId,
    reportPolicyType,
    reportZoneId,
    scopedOrgId,
    scopedSiteId,
  ]);

  useEffect(() => {
    if (!scope.enabled) {
      setReviewScopeIncidents([]);
      setReviewScopeIncidentsLoading(false);
      return;
    }

    let cancelled = false;
    setReviewScopeIncidentsLoading(true);

    incidentsApi.listIncidents(
      undefined,
      undefined,
      100,
      0,
      scope.fromIso,
      scope.toIso,
      scope.incidentType,
      scope.zoneId,
      scope.deviceId,
    ).then((response) => {
      if (cancelled) return;
      const incidents = Array.isArray(response?.incidents)
        ? response.incidents.filter((item: any) => item && typeof item === 'object')
        : [];
      setReviewScopeIncidents(incidents);
    }).catch(() => {
      if (cancelled) return;
      setReviewScopeIncidents([]);
    }).finally(() => {
      if (!cancelled) setReviewScopeIncidentsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!scope.enabled) {
      setReviewOutcomeRollup(null);
      setReviewOutcomeRollupLoading(false);
      setReviewOutcomeHistory(null);
      setReviewOutcomeHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setReviewOutcomeRollupLoading(true);

    incidentsApi.getReviewOutcomeRollup({
      org_id: scope.orgId,
      site_id: scope.siteId,
      from_ts: scope.fromIso,
      to_ts: scope.toIso,
      zone_id: scope.zoneId,
      device_id: scope.deviceId,
    }).then((response) => {
      if (cancelled) return;
      setReviewOutcomeRollup(response);
    }).catch(() => {
      if (cancelled) return;
      setReviewOutcomeRollup(null);
    }).finally(() => {
      if (!cancelled) setReviewOutcomeRollupLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!scope.enabled) {
      setReviewOutcomeHistory(null);
      setReviewOutcomeHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setReviewOutcomeHistoryLoading(true);

    incidentsApi.getReviewOutcomeHistory({
      org_id: scope.orgId,
      site_id: scope.siteId,
      from_ts: scope.fromIso,
      to_ts: scope.toIso,
      zone_id: scope.zoneId,
      device_id: scope.deviceId,
    }).then((response) => {
      if (cancelled) return;
      setReviewOutcomeHistory(response);
    }).catch(() => {
      if (cancelled) return;
      setReviewOutcomeHistory(null);
    }).finally(() => {
      if (!cancelled) setReviewOutcomeHistoryLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!scope.enabled) {
      setOwnershipRollup(null);
      setOwnershipRollupLoading(false);
      return;
    }

    let cancelled = false;
    setOwnershipRollupLoading(true);

    incidentsApi.getOwnershipRollup({
      org_id: scope.orgId,
      site_id: scope.siteId,
      from_ts: scope.fromIso,
      to_ts: scope.toIso,
      zone_id: scope.zoneId,
      device_id: scope.deviceId,
    }).then((response) => {
      if (cancelled) return;
      setOwnershipRollup(response);
    }).catch(() => {
      if (cancelled) return;
      setOwnershipRollup(null);
    }).finally(() => {
      if (!cancelled) setOwnershipRollupLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!scope.enabled) {
      setEscalationRollup(null);
      setEscalationRollupLoading(false);
      return;
    }

    let cancelled = false;
    setEscalationRollupLoading(true);

    incidentsApi.getEscalationRollup({
      org_id: scope.orgId,
      site_id: scope.siteId,
      from_ts: scope.fromIso,
      to_ts: scope.toIso,
      zone_id: scope.zoneId,
      device_id: scope.deviceId,
    }).then((response) => {
      if (cancelled) return;
      setEscalationRollup(response);
    }).catch(() => {
      if (cancelled) return;
      setEscalationRollup(null);
    }).finally(() => {
      if (!cancelled) setEscalationRollupLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!scope.enabled) {
      setCorrectiveActionRollup(null);
      setCorrectiveActionRollupLoading(false);
      return;
    }

    let cancelled = false;
    setCorrectiveActionRollupLoading(true);

    incidentsApi.getCorrectiveActionRollup({
      org_id: scope.orgId,
      site_id: scope.siteId,
      from_ts: scope.fromIso,
      to_ts: scope.toIso,
      zone_id: scope.zoneId,
      device_id: scope.deviceId,
    }).then((response) => {
      if (cancelled) return;
      setCorrectiveActionRollup(response);
    }).catch(() => {
      if (cancelled) return;
      setCorrectiveActionRollup(null);
    }).finally(() => {
      if (!cancelled) setCorrectiveActionRollupLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!scope.enabled) {
      setMeasurementRollup(null);
      setMeasurementRollupLoading(false);
      return;
    }

    let cancelled = false;
    setMeasurementRollupLoading(true);

    incidentsApi.getMeasurementRollup({
      org_id: scope.orgId,
      site_id: scope.siteId,
      from_ts: scope.fromIso,
      to_ts: scope.toIso,
      zone_id: scope.zoneId,
      device_id: scope.deviceId,
    }).then((response) => {
      if (cancelled) return;
      setMeasurementRollup(response);
    }).catch(() => {
      if (cancelled) return;
      setMeasurementRollup(null);
    }).finally(() => {
      if (!cancelled) setMeasurementRollupLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!scope.enabled) {
      setActionSnapshotRollup(null);
      setActionSnapshotRollupLoading(false);
      return;
    }

    let cancelled = false;
    setActionSnapshotRollupLoading(true);

    incidentsApi.getActionSnapshotRollup({
      org_id: scope.orgId,
      site_id: scope.siteId,
      from_ts: scope.fromIso,
      to_ts: scope.toIso,
      zone_id: scope.zoneId,
      device_id: scope.deviceId,
    }).then((response) => {
      if (cancelled) return;
      setActionSnapshotRollup(response);
    }).catch(() => {
      if (cancelled) return;
      setActionSnapshotRollup(null);
    }).finally(() => {
      if (!cancelled) setActionSnapshotRollupLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  return {
    reviewScopeIncidents,
    reviewScopeIncidentsLoading,
    reviewOutcomeRollup,
    reviewOutcomeRollupLoading,
    reviewOutcomeHistory,
    reviewOutcomeHistoryLoading,
    ownershipRollup,
    ownershipRollupLoading,
    correctiveActionRollup,
    correctiveActionRollupLoading,
    measurementRollup,
    measurementRollupLoading,
    actionSnapshotRollup,
    actionSnapshotRollupLoading,
    escalationRollup,
    escalationRollupLoading,
  };
}
